/** @jsxImportSource @emotion/react */
import { ContentSection } from 'components/layouts/ContentSection';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { Code } from 'models/common/CommonCode';
import { SetupEquipmentCondition } from 'models/setup/SetupEquipment';
import { SetupProject } from 'models/setup/SetupProject';
import ZoneCoordinateGrid from 'pages/setup/mobile/mapzonmanagement/ZoneCoordinateGrid';
import ZoneGroupGrid from 'pages/setup/mobile/mapzonmanagement/ZoneGroupGrid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findProject } from 'apis/setup/SetupProject';
import useSessionStore from 'stores/useSessionStore';

const SetupMapZonManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { elmPrjId } = useSessionStore();

  const [searchPrjId, setSearchPrjId] = useState<string>('');

  const [searchMapZonId, setSearchMapZonId] = useState<string>('');
  const [pZonCd, setPZonCd] = useState<string>('');
  const [pZonNm, setPZonNm] = useState<string>('');
  const [searchParamData, setSearchParamData] = useState<boolean>(false);
  const [searchState, setSearchState] = useState<boolean>(false);

  const [selectedProjectId, setSelectedProjectId] = useState<string>(elmPrjId || '');

  // Plant-related states (from BoxLocation)
  const defaultCondition = {
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjId: elmPrjId || '',
    elmPrjNm: '',
    searchDataInsUserId: '',
  };
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [setupEquipmentCondition, setSetupEquipmentCondition] =
    useState<SetupEquipmentCondition>(defaultCondition);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);

  const btnSearch = () => {
    // 프로젝트 선택 여부 확인
    if (!selectedProjectId) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.프로젝트를 선택한 후 조회해주세요.',
          '프로젝트를 선택한 후 조회해주세요.'
        ),
      });
      return;
    }

    setSearchPrjId(selectedProjectId);
    setSearchMapZonId('');
    setSearchParamData(true);
    setSearchState((prev) => !prev);
  };

  // 초기 코드 및 프로젝트 목록 조회
  useEffect(() => {
    initCodes();
  }, []);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);

    findProject().then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  // 프로젝트 옵션 (ComboBox용) - filtered by plant selection
  const projectOptions = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) => {
        // elmPrjId와 elmPrjNm이 있는 항목만 포함
        if (!o.elmPrjId || !o.elmPrjNm) return false;
        // 플랜트 필터 적용
        return (setupEquipmentCondition?.bildPlntCds || []).length > 0
          ? setupEquipmentCondition.bildPlntCds.includes(o.bildPlntCd)
          : true;
      })
      .map((cur) => ({
        cmnCd: cur.elmPrjId!,
        cmnCdNm: cur.elmPrjNm!,
      }));
  }, [prjMasterData, setupEquipmentCondition?.bildPlntCds]);

  // 통합 변경 핸들러 (handleOnChange)
  const handleOnChange = (name: string, value: any) => {
    setSetupEquipmentCondition((prev) => ({ ...prev, [name]: value }));

    if (name === 'elmPrjId') {
      setSelectedProjectId(value);
    }
  };

  const listener = useCallback((pMapZonId: string, pZonCd: string, pZonNm: string) => {
    setSearchMapZonId(pMapZonId);
    setPZonCd(pZonCd);
    setPZonNm(pZonNm);
  }, []);

  const btnReload = () => {
    setSetupEquipmentCondition(defaultCondition);
    setSelectedProjectId('');
    setSearchPrjId('');
    setSearchMapZonId('');
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupEquipmentCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    // 플랜트 선택 변경 시, 현재 선택된 프로젝트가 새로운 플랜트 필터에 포함되지 않으면 프로젝트 선택 초기화
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupEquipmentCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <ComboBox
                  placeholder={String(
                    t('com.label.프로젝트를 선택해주세요', '프로젝트를 선택해주세요')
                  )}
                  options={projectOptions}
                  defaultValue={setupEquipmentCondition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setSetupEquipmentCondition}
              onSearch={btnSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <ContentSection>
        <ZoneGroupGrid
          sPrjId={searchPrjId}
          sZonNm=""
          sUseYn=""
          editable={true}
          callBack={listener}
          searchParamData={searchParamData}
          searchState={searchState}
          projectOptions={projectOptions}
        />
      </ContentSection>
      <ContentSection>
        <ZoneCoordinateGrid
          sMapZonId={searchMapZonId}
          editable={true}
          sZonCd={pZonCd}
          sZonNm={pZonNm}
        />
      </ContentSection>
    </>
  );
};

export default SetupMapZonManagementPage;
